import React, { useState } from 'react';
import { 
  FaFileContract,
  FaBalanceScale,
  FaUserCheck,
  FaShieldAlt,
  FaExclamationTriangle,
  FaCheckCircle,
  FaPhone,
  FaWhatsapp,
  FaEnvelope,
  FaArrowRight,
  FaBook,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaCar,
  FaHotel,
  FaPlane,
  FaShip
} from 'react-icons/fa';
import '../style.css';

const TermsServices = () => {
  const [tosActiveSection, setTosActiveSection] = useState('general');

  const tosSections = [
    {
      id: 'general',
      title: 'General Terms',
      icon: <FaFileContract />,
      color: '#8B4513'
    },
    {
      id: 'booking',
      title: 'Booking Terms',
      icon: <FaCalendarAlt />,
      color: '#D4AF37'
    },
    {
      id: 'payment',
      title: 'Payment Terms',
      icon: <FaMoneyBillWave />,
      color: '#2ecc71'
    },
    {
      id: 'cancellation',
      title: 'Cancellation',
      icon: <FaExclamationTriangle />,
      color: '#e74c3c'
    },
    {
      id: 'liability',
      title: 'Liability',
      icon: <FaShieldAlt />,
      color: '#3498db'
    },
    {
      id: 'privacy',
      title: 'Privacy',
      icon: <FaUserCheck />,
      color: '#9b59b6'
    }
  ];

  const tosGeneralTerms = [
    {
      title: 'Acceptance of Terms',
      content: `By accessing and using Shree Balaji Travels' services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. These terms govern all services provided including but not limited to taxi services, tour packages, hotel bookings, and pilgrimage services.`
    },
    {
      title: 'Service Description',
      content: `Shree Balaji Travels provides comprehensive travel services in Varanasi including:
      • Taxi and cab services for local and outstation travel
      • Tour packages for religious and cultural destinations
      • Hotel and accommodation bookings
      • Pind Daan and pilgrimage services
      • Cruise bookings on River Ganges
      • Air ticket reservations`
    },
    {
      title: 'Eligibility',
      content: `You must be at least 18 years old to use our services. By using our services, you represent that you are at least 18 years of age and have the legal capacity to enter into a binding contract. For bookings made on behalf of minors, the guardian accepts full responsibility.`
    },
    {
      title: 'Modification of Terms',
      content: `We reserve the right to modify these terms at any time. Any changes will be effective immediately upon posting on our website. Continued use of our services after changes constitutes acceptance of the modified terms.`
    }
  ];

  const tosBookingTerms = [
    {
      title: 'Booking Procedure',
      content: `• Bookings can be made via phone, WhatsApp, email, or in-person at our office
      • All bookings require confirmation from our team
      • Advance booking is recommended, especially during peak seasons
      • Same-day bookings subject to availability
      • Minimum 4 hours notice required for airport/railway pickups`
    },
    {
      title: 'Booking Confirmation',
      content: `• Booking is confirmed only after receiving payment/advance
      • Confirmation will be sent via SMS/WhatsApp/Email
      • Please verify all booking details upon confirmation
      • Report any discrepancies within 1 hour of confirmation`
    },
    {
      title: 'Documentation',
      content: `• Valid government ID proof required for all bookings
      • Foreign nationals must provide passport details
      • Special requirements (wheelchair, child seat) must be specified at booking
      • Contact details must be accurate and functional`
    },
    {
      title: 'Vehicle Allocation',
      content: `• Vehicle allocation is based on availability at time of booking
      • We reserve the right to provide equivalent or superior vehicle
      • Specific vehicle requests are subject to availability
      • Minimum 2 hours notice required for vehicle changes`
    }
  ];

  const tosPaymentTerms = [
    {
      title: 'Payment Methods',
      content: `We accept the following payment methods:
      • Cash (INR)
      • UPI (PhonePe, Google Pay, Paytm)
      • Credit/Debit Cards
      • Bank Transfer
      • Cheque (subject to clearance)`
    },
    {
      title: 'Advance Payment',
      content: `• 50% advance required for bookings above ₹5,000
      • 100% advance for same-day bookings
      • Full payment required for festival/peak season bookings
      • Corporate clients: 25% advance with approved credit`
    },
    {
      title: 'GST & Taxes',
      content: `• All prices are subject to 5% GST
      • Toll charges, parking fees, and state taxes extra
      • Driver allowance extra for outstation trips (beyond 250 km)
      • Night charges applicable from 10 PM to 6 AM`
    },
    {
      title: 'Payment Timeline',
      content: `• Local trips: Payment at end of journey
      • Outstation trips: 50% advance, balance before departure
      • Tour packages: As per package terms
      • Delayed payments attract 1.5% monthly interest`
    }
  ];

  const tosCancellationTerms = [
    {
      title: 'Cancellation Charges',
      content: `• More than 24 hours: Full refund (minus ₹100 processing fee)
      • 12-24 hours: 50% of advance refunded
      • Less than 12 hours: No refund
      • No-show: Full booking amount forfeited
      • Natural calamities: Full refund`
    },
    {
      title: 'Refund Process',
      content: `• Refunds processed within 7 working days
      • Refund to original payment method only
      • GST amount non-refundable
      • Refund status available via booking ID
      • Contact customer care for refund queries`
    },
    {
      title: 'Modification Policy',
      content: `• Date/Time changes: Free if made 6+ hours in advance
      • Vehicle upgrade: Pay difference amount
      • Vehicle downgrade: No refund for difference
      • Route changes: Charges may apply
      • Contact for any modifications`
    }
  ];

  const tosLiabilityTerms = [
    {
      title: 'Service Liability',
      content: `• We ensure well-maintained vehicles with valid documents
      • Drivers are licensed and trained professionals
      • We are not liable for traffic delays, road conditions, or weather
      • Maximum liability limited to booking amount
      • Force majeure events excluded`
    },
    {
      title: 'Customer Responsibility',
      content: `• Provide accurate pickup/drop details
      • Be ready at scheduled pickup time
      • Inform about delays immediately
      • Treat vehicle and driver with respect
      • Report any issues immediately`
    },
    {
      title: 'Personal Belongings',
      content: `• We are not responsible for lost/damaged personal belongings
      • Valuables should not be left in vehicles
      • Report missing items within 1 hour of trip completion
      • CCTV footage available for verification`
    },
    {
      title: 'Insurance',
      content: `• All vehicles have comprehensive insurance
      • Passengers covered under vehicle insurance
      • Additional travel insurance available on request
      • Claims subject to insurance company terms`
    }
  ];

  const tosPrivacyTerms = [
    {
      title: 'Data Collection',
      content: `We collect:
      • Personal information (name, contact details)
      • Travel information (dates, destinations)
      • Payment information (securely processed)
      • Communication records
      • Usage data from our website`
    },
    {
      title: 'Data Usage',
      content: `Your data is used for:
      • Service delivery and booking processing
      • Communication and updates
      • Service improvement
      • Legal compliance
      • Marketing (with consent)`
    },
    {
      title: 'Data Protection',
      content: `• Secure SSL encryption for all transactions
      • Restricted access to personal data
      • Regular security audits
      • Compliance with Indian data protection laws
      • Data retention as per legal requirements`
    },
    {
      title: 'Third-Party Sharing',
      content: `We may share data with:
      • Service providers (for service delivery)
      • Legal authorities (when required)
      • Payment gateways (for transaction processing)
      • Insurance companies (for claims)
      We do not sell your personal information.`
    }
  ];

  const getTosActiveContent = () => {
    switch(tosActiveSection) {
      case 'general': return tosGeneralTerms;
      case 'booking': return tosBookingTerms;
      case 'payment': return tosPaymentTerms;
      case 'cancellation': return tosCancellationTerms;
      case 'liability': return tosLiabilityTerms;
      case 'privacy': return tosPrivacyTerms;
      default: return tosGeneralTerms;
    }
  };

  const getTosSectionTitle = () => {
    return tosSections.find(s => s.id === tosActiveSection)?.title || 'Terms of Service';
  };

  return (
    <div className="tos-wrapper">
      {/* Header */}
      <div className="tos-hero-section">
        <div className="tos-hero-content">
          <h1 className="tos-main-title">Terms of Service</h1>
          <p className="tos-subtitle">Shree Balaji Travels - Varanasi</p>
          <div className="tos-badge-container">
            <span className="tos-badge-item">
              <FaBalanceScale /> Legal Agreement
            </span>
            <span className="tos-badge-item">
              <FaCheckCircle /> Transparent Policies
            </span>
            <span className="tos-badge-item">
              <FaShieldAlt /> Customer Protection
            </span>
          </div>
        </div>
      </div>

      <div className="tos-content-wrapper">
        {/* Side Navigation */}
        <div className="tos-side-nav">
          <div className="tos-nav-panel">
            <h3 className="tos-nav-title">Terms Sections</h3>
            {tosSections.map((section) => (
              <button
                key={section.id}
                className={`tos-nav-button ${tosActiveSection === section.id ? 'tos-nav-active' : ''}`}
                onClick={() => setTosActiveSection(section.id)}
                style={{ '--tos-btn-color': section.color }}
              >
                <span className="tos-button-icon" style={{ color: section.color }}>
                  {section.icon}
                </span>
                {section.title}
                {tosActiveSection === section.id && <FaArrowRight className="tos-nav-arrow" />}
              </button>
            ))}
          </div>

          {/* Contact Box */}
          <div className="tos-contact-panel">
            <h4 className="tos-contact-title">Questions?</h4>
            <p className="tos-contact-text">Contact us for clarification</p>
            <div className="tos-contact-methods">
              <a href="tel:+919415799879" className="tos-contact-link">
                <FaPhone /> +91 9415799879
              </a>
              <a href="https://wa.me/919415799879" className="tos-contact-link">
                <FaWhatsapp /> WhatsApp
              </a>
              <a href="mailto:legal@shreebalajitravels.com" className="tos-contact-link">
                <FaEnvelope /> Legal Email
              </a>
            </div>
          </div>

          {/* Quick Info */}
          <div className="tos-info-box">
            <h4 className="tos-info-title">Quick Info</h4>
            <div className="tos-info-items">
              <div className="tos-info-item">
                <FaBook />
                <span>Last Updated: {new Date().toLocaleDateString('en-IN')}</span>
              </div>
              <div className="tos-info-item">
                <FaFileContract />
                <span>Version: 3.0</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="tos-main-content">
          <div className="tos-content-header">
            <div className="tos-title-wrapper">
              {tosSections.find(s => s.id === tosActiveSection)?.icon}
              <h2 className="tos-content-title">{getTosSectionTitle()}</h2>
            </div>
            <p className="tos-content-subtitle">
              These terms constitute a legal agreement between you and Shree Balaji Travels
            </p>
          </div>

          {/* Terms Cards */}
          <div className="tos-cards-container">
            {getTosActiveContent().map((item, index) => (
              <div key={index} className="tos-card-item">
                <div className="tos-card-header">
                  <h3 className="tos-card-title">{item.title}</h3>
                </div>
                <div className="tos-card-body">
                  {item.content.split('\n').map((line, i) => (
                    <p key={i} className="tos-text-line">
                      {line.trim().startsWith('•') ? (
                        <>
                          <span className="tos-bullet">•</span>
                          {line.substring(1).trim()}
                        </>
                      ) : (
                        line
                      )}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Acceptance Section */}
          <div className="tos-acceptance-box">
            <div className="tos-acceptance-icon">
              <FaUserCheck />
            </div>
            <div className="tos-acceptance-content">
              <h4 className="tos-acceptance-title">Acceptance of Terms</h4>
              <p className="tos-acceptance-text">
                By using our services, you acknowledge that you have read, understood, 
                and agree to be bound by these Terms of Service. If you do not agree 
                to these terms, please do not use our services.
              </p>
            </div>
          </div>

          {/* Service Icons */}
          <div className="tos-services-box">
            <h4 className="tos-services-title">Services Covered</h4>
            <div className="tos-service-icons">
              <div className="tos-service-icon">
                <FaCar />
                <span>Taxi Services</span>
              </div>
              <div className="tos-service-icon">
                <FaHotel />
                <span>Hotel Booking</span>
              </div>
              <div className="tos-service-icon">
                <FaPlane />
                <span>Air Tickets</span>
              </div>
              <div className="tos-service-icon">
                <FaShip />
                <span>Cruise Booking</span>
              </div>
              <div className="tos-service-icon">
                <FaBook />
                <span>Tour Packages</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="tos-footer-section">
        <div className="tos-footer-content">
          <div className="tos-footer-logo">
            <FaFileContract />
            <span>Terms of Service</span>
          </div>
          <p className="tos-footer-text">
            Shree Balaji Travels - Registered Office: D.58/12-A, Plot No 84, Gandhi Nagar, Sigra, Varanasi
          </p>
          <div className="tos-footer-notice">
            <FaExclamationTriangle />
            <p className="tos-notice-text">
              These terms are legally binding. For any disputes, jurisdiction lies in Varanasi courts.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsServices;